import { useMapContext } from '@components/map/MapContext';
import { useMapLayerMouseEvent } from '@hooks/useMapLayerMouseEvent';
import { useSelectorWithMapId } from '@hooks/useSelectorWithMapId';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { closePopupByMapIdAndType, updateMenuNavigationStatus } from '@redux/maps/maps.reducer';
import { selectMapNavigationOptions } from '@redux/maps/maps.selectors';
import { toCoordinates } from '@utils/validation/coordinates.utils';
import { Position } from 'geojson';
import { MapEventOf } from 'mapbox-gl';
import { useCallback, useEffect } from 'react';
import { useMap } from 'react-map-gl/mapbox';

import { Coordinates, NullableCoordinates } from '@/types/commons/commons.types';
import { LayerNameEnum } from '@/types/map.types';
import { PopupTypeEnum } from '@/types/popup.types';

const measureLayers = [LayerNameEnum.MEASURE_LINES, LayerNameEnum.MEASURE_POINTS, LayerNameEnum.MEASURE_POINT_LABELS];

export const useMapMeasure = (maxPoints = 2): [Coordinates | NullableCoordinates | null, Position[]] => {
  const { current: mapbox } = useMap();
  const {
    mapId,
    mousePosition,
    measurePoints,
    isClickEnabled,
    isMeasureActive,
    setIsClickEnabled,
    setIsMeasureActive,
    setMousePosition,
    setMeasurePoints,
  } = useMapContext();
  const selectedToolMenuOptions = useSelectorWithMapId(selectMapNavigationOptions);

  const closePopupByType = useWithDispatch(closePopupByMapIdAndType);
  const dispatchSelectedOptions = useWithDispatch(updateMenuNavigationStatus);

  const handleMouseMove = useCallback(
    (event: MapEventOf<'mousemove'>) => {
      if (mapbox && isMeasureActive && isClickEnabled) {
        mapbox.getCanvas().style.cursor = 'crosshair';
        const mousePosition = toCoordinates(event.lngLat);
        if (mousePosition) {
          setMousePosition(mousePosition);
        }
      }
    },
    [mapbox, setMousePosition, isMeasureActive, isClickEnabled],
  );

  const handleMeasure = useCallback(
    (event: MapEventOf<'click'>) => {
      if (!mapbox || !isMeasureActive || !isClickEnabled) return;

      const newPoint: Position = [event.lngLat.lng, event.lngLat.lat];

      if (measurePoints.length < maxPoints) {
        setMeasurePoints([...measurePoints, newPoint]);
      }
      if (measurePoints.length + 1 === maxPoints) {
        setIsClickEnabled(false);
        mapbox.getCanvas().style.cursor = 'auto';
      }
    },
    [mapbox, isMeasureActive, isClickEnabled, maxPoints, measurePoints, setMeasurePoints, setIsClickEnabled],
  );

  const handleClearDrawing = useCallback(
    (event: MapEventOf<'click'>) => {
      if (isClickEnabled) return;

      if (mapbox) {
        const features = mapbox.queryRenderedFeatures(event.point, {
          layers: measureLayers,
        });

        if (features.length > 0) {
          setMeasurePoints([]);
          setIsMeasureActive(false);
          closePopupByType({ mapId, type: PopupTypeEnum.MEASURE_INFO });
          const newSelectedOptions = structuredClone(selectedToolMenuOptions ?? []);
          if (newSelectedOptions.includes('azimuthAndDistance')) {
            dispatchSelectedOptions({
              mapId,
              selectedOptions: newSelectedOptions.filter((opt) => opt !== 'azimuthAndDistance'),
            });
          }
        }
      }
    },
    [
      isClickEnabled,
      mapbox,
      setMeasurePoints,
      setIsMeasureActive,
      closePopupByType,
      mapId,
      selectedToolMenuOptions,
      dispatchSelectedOptions,
    ],
  );

  useEffect(() => {
    if (!isMeasureActive) {
      setIsClickEnabled(true);
      setMeasurePoints([]);
    }

    return () => {
      if (mapbox) {
        const canvas = mapbox.getCanvas();
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!canvas) return;

        canvas.style.cursor = 'auto';
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapbox, isMeasureActive]);

  useMapLayerMouseEvent('mousemove', handleMouseMove);
  useMapLayerMouseEvent('click', handleMeasure);

  useMapLayerMouseEvent('click', handleClearDrawing);

  return [mousePosition, measurePoints];
};
